import {
  ORDER_EVENT_STATUS_ACCEPTED,
  ORDER_EVENT_STATUS_APPROVED,
  ORDER_EVENT_STATUS_COMPLETED,
  ORDER_EVENT_STATUS_DENIED,
  ORDER_EVENT_STATUS_DRAFT,
  ORDER_EVENT_STATUS_EXPIRED,
  ORDER_EVENT_STATUS_FUNDED,
  ORDER_EVENT_STATUS_PUBLISHED,
  ORDER_EVENT_STATUS_REJECTED,
  ORDER_EVENT_STATUS_VOIDED
} from '@klear/constants/order-event-status'
import {
  PERMISSION_ACTION_ACCEPT,
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_DENY,
  PERMISSION_ACTION_EXPIRE,
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ACTION_REJECT,
  PERMISSION_ACTION_RESET,
  PERMISSION_ACTION_VOID,
  PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_LATE_FEE,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PAAS_OFFER,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ENTITY_PLATFORM_FEE,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import keys from 'lodash/keys'
import uniq from 'lodash/uniq'

export const ORDER_EVENT_LIFECYCLE_STATES = [
  ORDER_EVENT_STATUS_DRAFT,
  ORDER_EVENT_STATUS_PUBLISHED,
  ORDER_EVENT_STATUS_APPROVED,
  ORDER_EVENT_STATUS_DENIED,
  ORDER_EVENT_STATUS_ACCEPTED,
  ORDER_EVENT_STATUS_REJECTED,
  ORDER_EVENT_STATUS_FUNDED,
  ORDER_EVENT_STATUS_COMPLETED,
  ORDER_EVENT_STATUS_EXPIRED,
  ORDER_EVENT_STATUS_VOIDED
] as const

export type OrderEventLifecycleState = (typeof ORDER_EVENT_LIFECYCLE_STATES)[number]

export const ORDER_EVENT_LIFECYCLE_ACTIONS = [
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_DENY,
  PERMISSION_ACTION_ACCEPT,
  PERMISSION_ACTION_REJECT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_EXPIRE,
  PERMISSION_ACTION_RESET,
  PERMISSION_ACTION_VOID
] as const

export type OrderEventLifecycleAction = (typeof ORDER_EVENT_LIFECYCLE_ACTIONS)[number]

const LIFECYCLE_ACTION_TYPES = new Set<string>(ORDER_EVENT_LIFECYCLE_ACTIONS)

export function isOrderEventLifecycleAction(type: string): type is OrderEventLifecycleAction {
  return LIFECYCLE_ACTION_TYPES.has(type)
}

const ORDER_EVENT_INITIAL_STATE = ORDER_EVENT_STATUS_DRAFT

export function lifecycleActionPermission(
  action: OrderEventLifecycleAction,
  state: OrderEventLifecycleState | null | undefined
): OrderEventLifecycleAction {
  if (action === PERMISSION_ACTION_RESET && state === ORDER_EVENT_STATUS_DENIED) return PERMISSION_ACTION_DENY
  return action
}

export function lifecycleActionPermissionsFor(action: OrderEventLifecycleAction): OrderEventLifecycleAction[] {
  return uniq([action, ...ORDER_EVENT_LIFECYCLE_STATES.map((state) => lifecycleActionPermission(action, state))])
}

type TransitionsByState = Partial<
  Record<OrderEventLifecycleState, Partial<Record<OrderEventLifecycleAction, OrderEventLifecycleState>>>
>

const REVIEWABLE_TRANSITIONS = {
  [ORDER_EVENT_STATUS_DRAFT]: {
    [PERMISSION_ACTION_PUBLISH]: ORDER_EVENT_STATUS_PUBLISHED,
    [PERMISSION_ACTION_APPROVE]: ORDER_EVENT_STATUS_APPROVED,
    [PERMISSION_ACTION_DENY]: ORDER_EVENT_STATUS_DENIED
  },
  [ORDER_EVENT_STATUS_PUBLISHED]: {
    [PERMISSION_ACTION_APPROVE]: ORDER_EVENT_STATUS_APPROVED,
    [PERMISSION_ACTION_DENY]: ORDER_EVENT_STATUS_DENIED
  },
  [ORDER_EVENT_STATUS_APPROVED]: {
    [PERMISSION_ACTION_DENY]: ORDER_EVENT_STATUS_DENIED,
    [PERMISSION_ACTION_RESET]: ORDER_EVENT_STATUS_DRAFT
  },
  [ORDER_EVENT_STATUS_DENIED]: {
    [PERMISSION_ACTION_PUBLISH]: ORDER_EVENT_STATUS_PUBLISHED,
    [PERMISSION_ACTION_RESET]: ORDER_EVENT_STATUS_DRAFT
  }
} as const satisfies TransitionsByState

const INVOICE_TRANSITIONS = {
  [ORDER_EVENT_STATUS_DRAFT]: {
    [PERMISSION_ACTION_APPROVE]: ORDER_EVENT_STATUS_APPROVED,
    [PERMISSION_ACTION_DENY]: ORDER_EVENT_STATUS_DENIED,
    [PERMISSION_ACTION_EXPIRE]: ORDER_EVENT_STATUS_EXPIRED
  },
  [ORDER_EVENT_STATUS_APPROVED]: {
    [PERMISSION_ACTION_DENY]: ORDER_EVENT_STATUS_DENIED,
    [PERMISSION_ACTION_RESET]: ORDER_EVENT_STATUS_DRAFT,
    [PERMISSION_ACTION_EXPIRE]: ORDER_EVENT_STATUS_EXPIRED
  },
  [ORDER_EVENT_STATUS_DENIED]: {
    [PERMISSION_ACTION_APPROVE]: ORDER_EVENT_STATUS_APPROVED,
    [PERMISSION_ACTION_RESET]: ORDER_EVENT_STATUS_DRAFT,
    [PERMISSION_ACTION_EXPIRE]: ORDER_EVENT_STATUS_EXPIRED
  },
  [ORDER_EVENT_STATUS_EXPIRED]: {
    [PERMISSION_ACTION_APPROVE]: ORDER_EVENT_STATUS_APPROVED
  }
} as const satisfies TransitionsByState

const INVOICE_OFFER_TRANSITIONS = {
  [ORDER_EVENT_STATUS_DRAFT]: {
    [PERMISSION_ACTION_ACCEPT]: ORDER_EVENT_STATUS_ACCEPTED,
    [PERMISSION_ACTION_REJECT]: ORDER_EVENT_STATUS_REJECTED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_ACCEPTED]: {
    [PERMISSION_ACTION_FUND]: ORDER_EVENT_STATUS_FUNDED,
    [PERMISSION_ACTION_REJECT]: ORDER_EVENT_STATUS_REJECTED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_REJECTED]: {
    [PERMISSION_ACTION_ACCEPT]: ORDER_EVENT_STATUS_ACCEPTED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_FUNDED]: {
    [PERMISSION_ACTION_COMPLETE]: ORDER_EVENT_STATUS_COMPLETED
  },
  [ORDER_EVENT_STATUS_VOIDED]: {
    [PERMISSION_ACTION_RESET]: ORDER_EVENT_STATUS_DRAFT
  }
} as const satisfies TransitionsByState

const PAAS_OFFER_TRANSITIONS = {
  [ORDER_EVENT_STATUS_DRAFT]: {
    [PERMISSION_ACTION_ACCEPT]: ORDER_EVENT_STATUS_ACCEPTED,
    [PERMISSION_ACTION_REJECT]: ORDER_EVENT_STATUS_REJECTED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_ACCEPTED]: {
    [PERMISSION_ACTION_FUND]: ORDER_EVENT_STATUS_FUNDED,
    [PERMISSION_ACTION_REJECT]: ORDER_EVENT_STATUS_REJECTED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_FUNDED]: {
    [PERMISSION_ACTION_COMPLETE]: ORDER_EVENT_STATUS_COMPLETED
  },
  [ORDER_EVENT_STATUS_COMPLETED]: {},
  [ORDER_EVENT_STATUS_REJECTED]: {
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_VOIDED]: {}
} as const satisfies TransitionsByState

const PAYMENT_TRANSITIONS = {
  [ORDER_EVENT_STATUS_DRAFT]: {
    [PERMISSION_ACTION_PUBLISH]: ORDER_EVENT_STATUS_PUBLISHED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  },
  [ORDER_EVENT_STATUS_PUBLISHED]: {
    [PERMISSION_ACTION_COMPLETE]: ORDER_EVENT_STATUS_COMPLETED,
    [PERMISSION_ACTION_VOID]: ORDER_EVENT_STATUS_VOIDED
  }
} as const satisfies TransitionsByState

const OPERATIONAL_TRANSITIONS = {} as const satisfies TransitionsByState

const FEE_TRANSITIONS = {} as const satisfies TransitionsByState

export const ORDER_EVENT_LIFECYCLE = {
  [PERMISSION_ENTITY_PURCHASE_ORDER]: REVIEWABLE_TRANSITIONS,
  [PERMISSION_ENTITY_INVOICE]: INVOICE_TRANSITIONS,
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: REVIEWABLE_TRANSITIONS,
  [PERMISSION_ENTITY_INVOICE_OFFER]: INVOICE_OFFER_TRANSITIONS,
  [PERMISSION_ENTITY_PAAS_OFFER]: PAAS_OFFER_TRANSITIONS,
  [PERMISSION_ENTITY_OPERATIONAL]: OPERATIONAL_TRANSITIONS,
  [PERMISSION_ENTITY_PAYMENT]: PAYMENT_TRANSITIONS,
  [PERMISSION_ENTITY_PLATFORM_FEE]: FEE_TRANSITIONS,
  [PERMISSION_ENTITY_LATE_FEE]: FEE_TRANSITIONS,
  [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: FEE_TRANSITIONS
} as const satisfies Record<string, TransitionsByState>

export type OrderEventLifecycleType = keyof typeof ORDER_EVENT_LIFECYCLE

const LIFECYCLE_EVENT_TYPES = new Set<string>(keys(ORDER_EVENT_LIFECYCLE))

export function isOrderEventLifecycleType(type: string): type is OrderEventLifecycleType {
  return LIFECYCLE_EVENT_TYPES.has(type)
}

const INITIAL_STATE_BY_TYPE = {
  [PERMISSION_ENTITY_PLATFORM_FEE]: ORDER_EVENT_STATUS_PUBLISHED,
  [PERMISSION_ENTITY_LATE_FEE]: ORDER_EVENT_STATUS_PUBLISHED,
  [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: ORDER_EVENT_STATUS_PUBLISHED
} as const satisfies Partial<Record<OrderEventLifecycleType, OrderEventLifecycleState>>

type TransitionsFor<TType extends OrderEventLifecycleType> = (typeof ORDER_EVENT_LIFECYCLE)[TType]

type TransitionTargetsFor<TType extends OrderEventLifecycleType> = {
  [TState in keyof TransitionsFor<TType>]: TransitionsFor<TType>[TState][keyof TransitionsFor<TType>[TState]]
}[keyof TransitionsFor<TType>]

type InitialStateFor<TType extends OrderEventLifecycleType> = TType extends keyof typeof INITIAL_STATE_BY_TYPE
  ? (typeof INITIAL_STATE_BY_TYPE)[TType]
  : typeof ORDER_EVENT_INITIAL_STATE

export type OrderEventLifecycleStatesFor<TType extends OrderEventLifecycleType> = Extract<
  keyof TransitionsFor<TType> | TransitionTargetsFor<TType> | InitialStateFor<TType>,
  OrderEventLifecycleState
>

export type OrderEventStatusLabelsFor<TType extends OrderEventLifecycleType> = Record<
  OrderEventLifecycleStatesFor<TType>,
  string
>

const INITIAL_STATE_LOOKUP: Partial<Record<OrderEventLifecycleType, OrderEventLifecycleState>> = INITIAL_STATE_BY_TYPE

export function orderEventInitialState(eventType: OrderEventLifecycleType): OrderEventLifecycleState {
  return INITIAL_STATE_LOOKUP[eventType] ?? ORDER_EVENT_INITIAL_STATE
}

export function orderEventTransition(
  eventType: OrderEventLifecycleType,
  state: OrderEventLifecycleState,
  action: OrderEventLifecycleAction
): OrderEventLifecycleState | null {
  const forType = ORDER_EVENT_LIFECYCLE[eventType] as TransitionsByState
  return forType[state]?.[action as OrderEventLifecycleAction] ?? null
}

export function orderEventLifecycleActionsFrom(
  eventType: OrderEventLifecycleType,
  state: OrderEventLifecycleState
): OrderEventLifecycleAction[] {
  const forType = ORDER_EVENT_LIFECYCLE[eventType] as TransitionsByState
  const fromState = forType[state]
  if (!fromState) return []
  return ORDER_EVENT_LIFECYCLE_ACTIONS.filter((action) => fromState[action] != null)
}

export type DocumentReadiness = 'missing' | 'awaitingApproval' | 'waitingOnTrader' | 'approved'

export function documentReadiness(
  eventType: OrderEventLifecycleType,
  state: OrderEventLifecycleState | null | undefined
): DocumentReadiness {
  if (state == null) return 'missing'
  if (state === ORDER_EVENT_STATUS_APPROVED) return 'approved'
  if (state === ORDER_EVENT_STATUS_DENIED) return 'waitingOnTrader'
  return orderEventTransition(eventType, state, PERMISSION_ACTION_APPROVE) == null
    ? 'waitingOnTrader'
    : 'awaitingApproval'
}
